import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import AuthService from 'services/auth'
import cutileiApi, { UPLOAD_TIMEOUT } from 'services/cutileiApi'
import { getApiErrorMessage } from 'services/apiError'
import Select from 'components/Inputs/Select'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import submitOnEnter from 'utils/submitOnEnter'
import AddPictureModal from '../AddPictureModal'
import DraggableImage from './DraggableImage'
import catalogServceValidator from 'validators/catalogServiceValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { IoCameraOutline } from 'react-icons/io5'
import { Formik } from 'formik'

import {
  Form,
  FormField,
  TextArea,
  Row,
  Label,
  SubTitle,
  InfoText,
  WarningText,
  ErrorContainer,
  ImagesContainer,
  AddImageButton
} from './styles'

export default function CatalogServiceModal ({
  visible,
  catalogID,
  service = null,
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [loading, setLoading] = useState (true)
  const [errorMessage, setErrorMessage] = useState (null)
  const [businessServices, setBusinessServices] = useState ([])
  const [showAddPictureModal, setShowAddPictureModal] = useState (false)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (visible) getBusinessServices ()
  }, [visible])

  const getBusinessServices = async () => {
    try {
      const { data: services } = await cutileiApi.get (
        `/businesses/${businessId}/services?status=active&cutilei_services=false`,
        requestConfig
      )

      setBusinessServices (services.map (service => ({
        label: service.nickname || service.name,
        value: service
      })))
    } catch (error) {
      console.error (error)
    } finally {
      setLoading (false)
    }
  }

  const toggleAddPictureModal = () => setShowAddPictureModal (prev => !prev)

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{service ? `Editar serviço do catálogo` : 'Adicionar serviço ao catálogo'}</DialogTitle>
        </DialogHeader>
        {loading ? <DialogBody><Loading/></DialogBody> : (
          <Formik
            validationSchema={catalogServceValidator}
            initialValues={{
              'service': service ? { label: service.name, value: service } : null,
              'description': service ? service.description : '',
              'images': service ? service.images.map ((img, idx) => ({
                ...img,
                uniqueId: img.id || `temp-${Date.now ()}-${idx}`
              })) : []
            }}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                setErrorMessage (null)
                const formData = new FormData ()
                const existingImages = values.images.filter (img => img.image_url).map (img => img.image_url)
                const newImages = values.images.filter (img => img.file)
              
                const serviceData = {
                  service_id: values.service?.value.id,
                  description: values.description || '',
                  images: existingImages
                }

                formData.append ('service_data', JSON.stringify (serviceData))

                if (newImages.length > 0) {
                  newImages.forEach (image => {
                    formData.append ('images', image.file, image.file.name)
                  })
                }

                const { data: catalogService } = await cutileiApi.request ({
                  method: service ? 'put' : 'post',
                  url: `/catalogs/${catalogID}/services${service ? `/${service.id}` : ''}`,
                  data: formData,
                  // Upload/download nao cabe no timeout padrao: rede movel ruim leva mais que 30 s.
                  timeout: UPLOAD_TIMEOUT,
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                  }
                })

                setSubmitting (false)
                handleConfirm (catalogService)
                handleClose ()
              } catch (error) {
                setSubmitting (false)
                setErrorMessage (getApiErrorMessage (error, '$1'))
                console.error (error)
              }
            }}
          >
            {function AddCatalogServicesForm ({
              values,
              errors,
              touched,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
              handleChange,
              handleBlur,
              handleSubmit
            }) {
              const handlePictureConfirm = croppedImage => {
                if (values.images.length < 5) {
                  const imageWithId = {
                    ...croppedImage,
                    uniqueId: `temp-${Date.now()}-${values.images.length}`
                  }
                  setFieldValue ('images', [...values.images, imageWithId])
                }
              }

              const handleRemoveImage = imageIndex => {
                const updatedImages = values.images.filter ((_, i) => i !== imageIndex)
                setFieldValue ('images', updatedImages)
              }

              const sensors = useSensors (
                useSensor (PointerSensor, {
                  activationConstraint: {
                    distance: 8
                  }
                })
              )

              const handleDragEnd = (event) => {
                const { active, over } = event

                if (over && active.id !== over.id) {
                  const oldIndex = values.images.findIndex (img => img.uniqueId === active.id)
                  const newIndex = values.images.findIndex (img => img.uniqueId === over.id)

                  if (oldIndex !== -1 && newIndex !== -1) {
                    const reorderedImages = arrayMove (values.images, oldIndex, newIndex)
                    setFieldValue ('images', reorderedImages)
                  }
                }
              }

              return (
                <>
                  <DialogBody>
                    <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
                      <Row>
                        <FormField style={{flex: 2}}>
                          <Label>Serviço</Label>
                          <Select
                            name='service'
                            placeholder='Selecione um serviço...'
                            value={values.service}
                            options={businessServices}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            isClearable={true}
                          />
                          {touched.service && errors.service && (
                            <WarningText>{errors.service}</WarningText>
                          )}
                        </FormField>
                      </Row>
                      <Row>
                        <FormField>
                          <Label htmlFor='description'>
                            Descrição
                          </Label>
                          <TextArea
                            id='description'
                            placeholder='Descrição'
                            value={values.description}
                            rows={4}
                            onChange={handleChange ('description')} 
                            onBlur={handleBlur ('description')}
                          />
                          {touched.description && errors.description && (
                            <WarningText>
                              {errors.description}
                            </WarningText>
                          )}
                        </FormField>
                      </Row>
                      <FormField>
                        <SubTitle>Fotos do serviço</SubTitle>
                        <InfoText>
                          Adicione fotos para deixar seu serviço mais atrativo.
                        </InfoText>
                        <InfoText>
                          Você pode adicionar até 5 fotos. Arraste para reordenar.
                        </InfoText>
                        <ImagesContainer>
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                          >
                            <SortableContext
                              items={values.images.map ((img) => img.uniqueId)}
                              strategy={rectSortingStrategy}
                            >
                              {values.images.map ((image, imgIndex) => (
                                <DraggableImage
                                  key={image.uniqueId}
                                  id={image.uniqueId}
                                  image={image}
                                  index={imgIndex}
                                  onRemove={handleRemoveImage}
                                />
                              ))}
                            </SortableContext>
                          </DndContext>
                    
                          {values.images.length < 5 && (
                            <AddImageButton onClick={toggleAddPictureModal}>
                              <IoCameraOutline size={26} color='#FCC100'/>
                              <Label>Adicionar foto</Label>
                            </AddImageButton>
                          )}
                        </ImagesContainer>
                      </FormField>
                      {errorMessage && (
                        <ErrorContainer>
                          <WarningText>{errorMessage}</WarningText>
                        </ErrorContainer>
                      )}
                      <AddPictureModal
                        visible={showAddPictureModal}
                        title='Adicionar foto ao serviço'
                        onConfirm={handlePictureConfirm}
                        onClose={toggleAddPictureModal}
                      />
                    </Form>
                  </DialogBody>
                  <DialogFooter>
                    <SecondaryButton
                      label={service ? 'Salvar alterações' : 'Adicionar serviço'}
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      width={160}
                      style={{marginTop: 20}}
                    />
                  </DialogFooter>
                </>
              )
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}
